/*
 * Tonus header.
 *
 * Deliberately small. The navigation's hover, focus and submenu behaviour is
 * entirely CSS, because every previous attempt that put those in JavaScript
 * failed the same way: Chrome defers derived pointer boundary events during
 * continuous movement, so `pointerenter` arrived late or not at all when the
 * cursor swept along the bar.
 *
 * What is left here is the two things CSS cannot express:
 *
 *   1. Whether the sticky bar is currently over content, so it can grow a
 *      hairline. There is no CSS selector for "is stuck".
 *   2. Opening the mobile dialog, because showModal() is an imperative call.
 *
 * Focus trapping, Escape and the inert background come from the platform.
 * The wiring below adds light dismissal and releases the modal when the
 * desktop navigation takes over. Scroll locking remains a CSS :has() rule.
 */

if (!customElements.get('tonus-header')) {
  customElements.define(
    'tonus-header',
    class TonusHeader extends HTMLElement {
      /** @type {IntersectionObserver | null} */
      #observer = null;

      /** @type {Array<() => void>} */
      #teardown = [];

      connectedCallback() {
        this.#watchStuckState();
        this.#wireDialog();
      }

      /*
       * Section unload in the Theme Editor destroys the element without a page
       * load, so every observer and listener is released here. Leaving an
       * IntersectionObserver behind would keep writing to a detached node.
       */
      disconnectedCallback() {
        this.#observer?.disconnect();
        this.#observer = null;
        this.#teardown.forEach((fn) => fn());
        this.#teardown = [];
      }

      /**
       * Watches a 1px sentinel that sits in flow immediately above the header,
       * so "stuck" means literally "the top of the page has scrolled away".
       *
       * Watching the sticky element itself does not work: it is the first thing
       * in the scroll container, so at scroll 0 its top edge is already flush
       * with the container's top and every "is it fully visible" test reports
       * stuck before the page has moved. That was measured, not assumed —
       * data-stuck was present at scrollTop 0.
       */
      #watchStuckState() {
        if (!this.hasAttribute('data-sticky')) return;
        if (typeof IntersectionObserver !== 'function') return;

        const sentinel = document.querySelector('.tnav-sentinel');
        if (!sentinel) return;

        this.#observer = new IntersectionObserver(
          ([entry]) => {
            this.toggleAttribute('data-stuck', !entry.isIntersecting);
          },
          { root: this.#scrollRoot(), threshold: 0 }
        );

        this.#observer.observe(sentinel);
      }

      /**
       * The page scrolls inside `.page-wrapper`, not the document, so the
       * observer has to be told which box to measure against. Resolved by
       * reading computed overflow rather than hard-coding the class, so this
       * keeps working if the layout wrapper changes.
       *
       * @returns {Element | null} the scroll container, or null for the viewport
       */
      #scrollRoot() {
        let node = this.parentElement;

        while (node && node !== document.body) {
          const overflowY = getComputedStyle(node).overflowY;
          if (overflowY === 'auto' || overflowY === 'scroll') return node;
          node = node.parentElement;
        }

        return null;
      }

      #wireDialog() {
        const trigger = this.querySelector('[data-tnav-open]');
        const id = trigger?.getAttribute('aria-controls');
        const section = this.closest('.shopify-section');
        const dialog = id ? section?.querySelector(`#${CSS.escape(id)}`) : null;

        if (!trigger || !(dialog instanceof HTMLDialogElement)) return;

        const desktop = window.matchMedia('(min-width: 990px)');
        const inner = dialog.querySelector('.tmenu__inner');
        let backdropPress = null;

        const open = () => {
          if (dialog.open || desktop.matches) return;
          // Touch activation need not focus a button. Give the native dialog
          // an explicit return target without installing a second focus trap.
          trigger.focus({ preventScroll: true });
          dialog.showModal();
          dialog.scrollTop = 0;
          trigger.setAttribute('aria-expanded', 'true');
        };

        const close = () => {
          backdropPress = null;
          trigger.setAttribute('aria-expanded', 'false');
          if (dialog.open) dialog.close();
        };

        /*
         * `close` fires for the button, for Escape and for form submission
         * alike, so the expanded state is synced from the event rather than
         * from each of the paths that can cause it.
         */
        const onClose = () => {
          // The event is queued: a rapid reopen must retain its expanded state.
          trigger.setAttribute('aria-expanded', String(dialog.open));
          backdropPress = null;
        };

        const isBackdrop = (target) => target === dialog || target === inner;

        const onPointerDown = (event) => {
          backdropPress = event.isPrimary && event.button === 0 && isBackdrop(event.target)
            ? { x: event.clientX, y: event.clientY }
            : null;
        };

        const onPointerCancel = () => {
          backdropPress = null;
        };

        const onClick = (event) => {
          const press = backdropPress;
          backdropPress = null;
          if (!(event.target instanceof Element)) return;

          if (event.target.closest('[data-tnav-close]')) {
            close();
            return;
          }

          const link = event.target.closest('a[href]');
          if (
            link && !event.defaultPrevented &&
            !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
            (!link.target || link.target === '_self')
          ) {
            // Also releases the modal for same-page anchors and bfcache visits.
            close();
            return;
          }

          // Both ends of a tap must land on the empty surround. A drag or a
          // scroll that ends there must not accidentally dismiss the menu.
          if (
            press && isBackdrop(event.target) &&
            Math.hypot(event.clientX - press.x, event.clientY - press.y) < 8
          ) close();
        };

        const onViewportChange = () => {
          if (!desktop.matches || !dialog.open) return;
          close();
          // The burger is now hidden; restore focus to the persistent logo.
          this.querySelector('.tnav__logo')?.focus({ preventScroll: true });
        };

        trigger.addEventListener('click', open);
        dialog.addEventListener('close', onClose);
        dialog.addEventListener('pointerdown', onPointerDown);
        dialog.addEventListener('pointercancel', onPointerCancel);
        dialog.addEventListener('click', onClick);
        desktop.addEventListener('change', onViewportChange);

        this.#teardown.push(() => {
          trigger.removeEventListener('click', open);
          dialog.removeEventListener('close', onClose);
          dialog.removeEventListener('pointerdown', onPointerDown);
          dialog.removeEventListener('pointercancel', onPointerCancel);
          dialog.removeEventListener('click', onClick);
          desktop.removeEventListener('change', onViewportChange);
          close();
        });
      }
    }
  );
}
